import Text from '@cartwhl/ui/base/Text';

import type { ChainSummary } from '../../../cartographerStore';

type ChainListProps = {
  chainSummaries: ChainSummary[];
  presetName: string | null;
  boneCount: number;
  showTitle?: boolean;
};

const CHAIN_GROUP_ORDER = [
  { label: 'Core', match: (n: string) => n === 'Root' || n === 'Pelvis' },
  { label: 'Spine', match: (n: string) => n === 'Spine' || n === 'Neck' || n === 'Head' },
  { label: 'Legs', match: (n: string) => n.includes('Leg') || n.includes('Toe') || n.includes('Hock') },
  { label: 'Arms', match: (n: string) => n.includes('Arm') || n.includes('Clavicle') },
  { label: 'Fingers', match: (n: string) => /Thumb|Index|Middle|Ring|Pinky/.test(n) },
  { label: 'Other', match: () => true },
];

function confidenceColor(c: number): string {
  if (c >= 0.8) return 'text-green-50';
  if (c >= 0.5) return 'text-yellow-50';
  return 'text-red-50';
}

export default function ChainList({
  chainSummaries,
  presetName,
  boneCount,
  showTitle = true,
}: ChainListProps) {
  const remaining = new Set(chainSummaries.map((c) => c.name));
  const groups: { label: string; chains: ChainSummary[] }[] = [];

  for (const { label, match } of CHAIN_GROUP_ORDER) {
    const chains: ChainSummary[] = [];
    for (const cs of chainSummaries) {
      if (remaining.has(cs.name) && match(cs.name)) {
        chains.push(cs);
        remaining.delete(cs.name);
      }
    }
    if (chains.length > 0) groups.push({ label, chains });
  }

  return (
    <div className="flex flex-col gap-3 p-3">
      {showTitle && (
        <Text variant="studio/body/medium/sm" className="text-primary">
          Recognition
        </Text>
      )}

      {presetName && (
        <Text
          variant="studio/body/medium/xs"
          className="bg-green-10 text-green-50 rounded-xl px-2 py-1"
        >
          Detected: {presetName}
        </Text>
      )}

      <Text variant="studio/body/sm" className="text-secondary">
        {boneCount} bones &middot; {chainSummaries.length} chains detected
      </Text>

      {groups.map((group) => (
        <div key={group.label} className="last:mb-0">
          <Text
            variant="studio/body/medium/xs"
            className="text-gray-45 mb-1 uppercase tracking-wider"
          >
            {group.label}
          </Text>
          {group.chains.map((cs) => (
            <div
              key={cs.name}
              className="hover:bg-gray-15 dark:hover:bg-gray-75 flex items-center justify-between rounded-lg px-1.5 py-1 transition-colors"
            >
              <Text variant="studio/body/medium/xs" className="truncate text-primary">
                {cs.name}
              </Text>
              <Text
                variant="studio/body/medium/xs"
                className={`ml-2 shrink-0 ${confidenceColor(cs.confidence)}`}
              >
                {Math.round(cs.confidence * 100)}%
              </Text>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
